/**
 * @file: mentions.ts
 *
 * Compose-side helpers for chat `@mentions`. The composer detects an
 * `@` trigger and the screen tracks a flat list of {@link ActiveMention}
 * (one per chip the user inserted). Ranges are NOT tracked live — they
 * shift on every keystroke. Instead {@link resolveTextMentions} re-derives
 * them from the FINAL sent text at send time, which also drops any chip
 * whose `@<name>` token the user has since edited away.
 */

import {
  ChatMentionTarget,
  ChatMessageMention,
  ChatRoomActiveMember,
} from '@euler/model/chat';
import { IdType } from '@euler/model/common';
import { idEquals } from '@euler/utils';

/** A mention the user has inserted, before its range is resolved. */
export interface ActiveMention {
  target: ChatMentionTarget;
  /** The name shown in the chip (also the `@<displayName>` token text). */
  displayName: string;
}

/** A pickable mention target in the member panel. */
export interface MentionCandidate {
  target: ChatMentionTarget;
  displayName: string;
  avatarUrl?: string | null;
}

/** Display name used for the `@all` ("所有人") chip + token. */
export const MENTION_ALL_LABEL = '所有人';

/**
 * Detect a freshly-typed `@` that should open the member picker. Returns
 * the `@`'s index when a SINGLE `@` was just inserted (anywhere — WeChat
 * triggers regardless of the preceding char, including at the end of
 * existing text). Returns null for multi-char edits (paste) or non-`@`
 * input, so it never fires on a paste that happens to contain `@`.
 */
export function detectMentionAnchor(prev: string, next: string): number | null {
  if (next.length !== prev.length + 1) return null;
  let i = 0;
  while (i < prev.length && prev[i] === next[i]) i++;
  return next[i] === '@' ? i : null;
}

/**
 * Build the picker's candidate list from a room's active members,
 * excluding the viewer themselves. `@all` is offered separately by the
 * panel (it isn't a member row).
 */
export function mentionCandidatesFromMembers(
  members: ChatRoomActiveMember[],
  selfPlatformUserId: IdType | string,
): MentionCandidate[] {
  return members
    .filter(m => !idEquals(m.platformUserId, selfPlatformUserId))
    .map(m => ({
      // A bot is mentioned by its `botId` so the send writer can wake it;
      // humans by their surface triple. (Bots share the sentinel
      // platformUserId 0, so the triple can't address them.)
      target:
        m.type === 'bot' && m.botId
          ? { kind: 'bot' as const, botId: m.botId }
          : {
              kind: 'user' as const,
              platformUserId: m.platformUserId,
              appId: m.appId,
              appScope: m.appScope,
            },
      displayName: m.displayName || m.name || '成员',
      avatarUrl: m.avatarUrl ?? null,
    }));
}

/** A `@<name>` token ends at whitespace, end-of-text, or the next `@`. */
function endsAtTokenBoundary(text: string, end: number): boolean {
  if (end >= text.length) return true;
  const next = text[end];
  return next === '@' || /\s/.test(next);
}

/**
 * Resolve concrete `range`s for each active mention against the final
 * text. Scans left-to-right, assigning each mention the earliest
 * non-overlapping `@<displayName>` occurrence that ends on a token
 * boundary (so a short name like "@张伟" can't match INSIDE a longer
 * "@张伟明", which would corrupt both the target and the highlight). Two
 * mentions of the same name each claim a distinct token; a mention whose
 * token is no longer present (user edited/deleted it) is dropped.
 * Longest names are resolved first so they claim their own tokens before
 * a shorter prefix can. Output is sorted by start offset.
 */
export function resolveTextMentions(
  text: string,
  active: ActiveMention[],
): ChatMessageMention[] {
  const out: ChatMessageMention[] = [];
  const used: Array<[number, number]> = [];
  const ordered = [...active].sort(
    (a, b) => b.displayName.length - a.displayName.length,
  );
  for (const m of ordered) {
    const token = '@' + m.displayName;
    let from = 0;
    for (;;) {
      const idx = text.indexOf(token, from);
      if (idx < 0) break;
      const end = idx + token.length;
      const overlaps = used.some(([s, e]) => idx < e && s < end);
      if (!overlaps && endsAtTokenBoundary(text, end)) {
        out.push({
          range: [idx, end],
          target: m.target,
          displayName: m.displayName,
        });
        used.push([idx, end]);
        break;
      }
      from = idx + 1;
    }
  }
  out.sort((a, b) => a.range[0] - b.range[0]);
  return out;
}

/** A text run, flagged when it's the body of a mention chip. */
export interface MentionTextSegment {
  text: string;
  mention: boolean;
}

/**
 * Split `text` into plain / mention runs for highlighted rendering. Skips
 * out-of-bounds or overlapping ranges defensively (the server never
 * validates ranges).
 */
export function splitTextByMentions(
  text: string,
  mentions: ChatMessageMention[] | undefined,
): MentionTextSegment[] {
  if (!mentions || mentions.length === 0) return [{ text, mention: false }];
  const ranges = mentions
    .map(m => m.range)
    .filter(
      ([s, e]) =>
        Number.isFinite(s) &&
        Number.isFinite(e) &&
        s >= 0 &&
        e <= text.length &&
        s < e,
    )
    .sort((a, b) => a[0] - b[0]);
  const segments: MentionTextSegment[] = [];
  let cursor = 0;
  for (const [s, e] of ranges) {
    if (s < cursor) continue; // overlap — skip
    if (s > cursor)
      segments.push({ text: text.slice(cursor, s), mention: false });
    segments.push({ text: text.slice(s, e), mention: true });
    cursor = e;
  }
  if (cursor < text.length)
    segments.push({ text: text.slice(cursor), mention: false });
  return segments;
}

/**
 * A protected, ATOMIC mention block `[start, end)` in the composer's live
 * text — the `@<name>` tag plus its single trailing space. The caret may
 * only sit at `start` or `end`; deleting any char inside removes the whole
 * block (WeChat behavior).
 */
export interface MentionBlock {
  start: number;
  end: number;
}

/**
 * Resolve the atomic mention blocks in `text` for the given active
 * mentions, each extended to include its one trailing space when present.
 */
export function mentionBlocks(
  text: string,
  active: ActiveMention[],
): MentionBlock[] {
  return resolveTextMentions(text, active).map(m => {
    const tagEnd = m.range[1];
    return {
      start: m.range[0],
      end: text[tagEnd] === ' ' ? tagEnd + 1 : tagEnd,
    };
  });
}

/**
 * Snap a selection so neither endpoint lands INSIDE an atomic mention
 * block. A collapsed caret snaps to the nearer block edge (WeChat
 * tap-to-edge); a range expands outward to fully cover any block it cuts.
 * Returns endpoints unchanged when nothing needed snapping.
 */
export function snapSelectionOutOfBlocks(
  selection: { start: number; end: number },
  blocks: MentionBlock[],
): { start: number; end: number } {
  const collapsed = selection.start === selection.end;
  const snapPoint = (pos: number, grow: 'left' | 'right'): number => {
    for (const b of blocks) {
      if (pos > b.start && pos < b.end) {
        if (collapsed) return pos - b.start <= b.end - pos ? b.start : b.end;
        return grow === 'left' ? b.start : b.end;
      }
    }
    return pos;
  };
  return {
    start: snapPoint(selection.start, 'left'),
    end: snapPoint(selection.end, 'right'),
  };
}

/** Index of the first differing char — the removed char on a 1-char delete. */
export function firstDiffIndex(a: string, b: string): number {
  let i = 0;
  const n = Math.min(a.length, b.length);
  while (i < n && a[i] === b[i]) i++;
  return i;
}
